<template>
  <div>
    <input v-show="false" ref="excelInput" type="file" @change="readFile" accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" />
    <slot name="header"/>
    <div>
      <div class="grid md:flex flex-wrap gap-2 items-center mt-6 mb-4">
        <h5>
          <slot name="title">
            <i class="dot"></i> <span class="inline-flex items-center" v-html="tableTitle"></span>
          </slot>
        </h5>
        <div class="sm:ml-auto ">
          <vs-button v-if="!readonly && hasDuplicateData" class="w-full sm:w-auto mr-2" color="danger" type="border" @click="onClickDuplicateRemove">중복 데이터 삭제</vs-button>
          <vs-button v-if="!readonly" class="w-full sm:w-auto" color="secondary" type="border" @click="onClickCheckRemove">선택삭제</vs-button>
        </div>
      </div>
      <div class="w-full overflow-x-auto">
        <table v-if="readonly">
          <colgroup>
            <col width="40px"/>
            <col width="53px"/>
            <slot name="colgroup"></slot>
            <col width="94px"/>
          </colgroup>
          <thead>
            <th class="px-2" v-for="th in tableHeader" v-html="th" :key="`tableHeader_${th}`"></th>
          </thead>

          <tbody>
            <tr v-for="(excel, rowIndex) in excelData" :key="`excelData_${rowIndex}`">
              <td v-for="(columnName, columnIndex) in tableHeader.length" :key="`${rowIndex}_${excel[0]}_${columnIndex}`">
                <div>
                  <slot name="column"
                        :index="columnIndex"
                        :data="excel.values[columnIndex]"
                        :dataType="tableDataType[columnIndex]"
                  ></slot>
                  <p v-if="excel.errorMSG" class="errMsg">{{ excel.errorMSG[columnIndex] || ""}}</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>


        <table v-else>
          <colgroup>
            <col width="40px"/>
            <col width="53px"/>
            <slot name="colgroup">
              <col v-for="(columnName, index) in tableHeader.length -1" :key="`colgroup_${index}`" width="100px;" />
            </slot>
            <col width="94px"/>
          </colgroup>
          <thead>
          <th><vs-checkbox :value="0 < excelData.length && excelData.length === checkList.length" @click.native="checkAll()" class="check" /></th>
          <th class="px-2" v-for="th in tableHeader" v-html="th" :key="`tableHeader_${th}`"></th>
          <th>수정/삭제</th>
          </thead>

          <tbody>
            <tr v-for="(excel, rowIndex) in excelData" :key="`excelData_${rowIndex}`">
              <td><vs-checkbox :vs-value="rowIndex" v-model="checkList" class="check" /></td>
              <td v-for="(columnName, columnIndex) in tableHeader.length" :key="`${rowIndex}_${excel[0]}_${columnIndex}`">
                <div v-if="editExcelIndex === rowIndex" class="p-1">
                  <slot name="edit"
                        :index="columnIndex"
                        :rowIndex="rowIndex"
                        :rowData="editExcelData.values"
                        :data="editExcelData.values[columnIndex]"
                        :dataType="tableDataType[columnIndex]"
                        :funcSetEditExcelData="setEditExcelData">
                    <fragment v-if="columnIndex === 0">
                      {{editExcelData.values[columnIndex]}}
                    </fragment>
                    <fragment v-if="columnIndex === 1">
                      <field-selector class="w-full text-left" v-model="editExcelData.values[columnIndex].id" @select="(field) => onSelectField(rowIndex, columnIndex, field)"/>
                    </fragment>
                    <fragment v-if="columnIndex > 1">
                      <ml-input v-model="editExcelData.values[columnIndex]" class="w-full" />
                    </fragment>
                  </slot>
                </div>
                <div v-else>
                  <slot name="column"
                        :index="columnIndex"
                        :data="excel.values[columnIndex]"
                        :dataType="tableDataType[columnIndex]"
                  ></slot>
                  <p v-if="excel.errorMSG" class="errMsg">{{ excel.errorMSG[columnIndex] || ""}}</p>
                </div>
              </td>
              <td>
                <div class="flex justify-center gap-3">
                  <!-- 저장/닫기 -->
                  <fragment v-if="editExcelIndex === rowIndex">
                    <button @click="onClickFinishExcelData(rowIndex)">
                      <feather-icon icon="CheckIcon" svgClasses="h-5 w-5" />
                    </button>
                    <button @click="onClickCancelExcelData(rowIndex)">
                      <feather-icon icon="XIcon" svgClasses="h-5 w-5" />
                    </button>
                  </fragment>
                  <!-- 수정/삭제 -->
                  <fragment v-else>
                    <button @click="onClickEditExcelData(rowIndex)">
                      <feather-icon icon="Edit2Icon" svgClasses="h-5 w-5" />
                    </button>
                    <button @click="onClickRemoveExcelData(rowIndex)">
                      <feather-icon icon="Trash2Icon" svgClasses="h-5 w-5" />
                    </button>
                  </fragment>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import ExcelJS from "exceljs";
export default {
  components: {},
  props: {
    board: Object,
    header: Object,
    tableTitle: String,
    useFieldCell: Boolean, // post 의 stdInfo.field_name 을 엑셀의 사업장 데이터로 사용할 경우
    useCompanyCell: Boolean, // post 의 stdInfo.com_name 을 엑셀의 사업장 데이터로 사용할 경우
    downloadLink: String,
    readExcelData: Function,
    setSaveFormData: Function,
    value: [Object, Array],
    readonly: Boolean,

    tableHeader: {
      type: Array,
      default: () => [],
    },
    tableDataType: {
      type: Array,
      default: () => [],
    },
  },

  computed: {
    projectId() {
      return parseInt(this.$route.params.proj_id);
    },
    boardId() {
      return parseInt(this.$route.params.boardId);
    },
    // 중복 데이터 존재 여부 확인
    hasDuplicateData() {
      if (!this.excelData || this.excelData.length === 0) {
        return false;
      }

      // 모든 엑셀 데이터를 검사하여 "이미 등록된" 문구가 포함된 에러 메시지가 있는지 확인
      return this.excelData.some(excel => {
        return excel.errorMSG && Object.values(excel.errorMSG).some(msg => msg && msg.includes('이미 등록된'));
      });
    }
  },

  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(value) {
        if (value && value.length > 0) {
          this.excelData = value;
        }
      },
    },
    excelData: {
      deep: true,
      handler() {
        this.$emit("input", this.excelData);
      },
    },
  },

  data () {
    return {
      excelData: [],
      fields: [],
      companies: [],
      editExcelIndex: null,
      editExcelData: [],
      checkList: [],
    }
  },

  async mounted() {
    if (this.useFieldCell) {
      await this.loadFieldListAll();
    }
    if (this.useCompanyCell) {
      await this.loadCompanyListAll();
    }
  },
  methods: {
    // checkAll을 computed에서 method로 이동
    checkAll() {
      if (0 < this.excelData.length && this.excelData.length === this.checkList.length) {
        this.checkList = [];
      } else {
        this.checkList = this.excelData.map((_, i) => i);
      }
    },

    // 중복 데이터 삭제 기능
    onClickDuplicateRemove() {
      // 에러 메시지가 있는 행 찾기
      const duplicateIndexes = this.excelData.reduce((indexes, excel, index) => {
        // 에러 메시지가 있고, "이미 등록된" 문구가 포함된 경우 중복 데이터로 간주
        if (excel.errorMSG && Object.values(excel.errorMSG).some(msg => msg && msg.includes('이미 등록된'))) {
          indexes.push(index);
        }
        return indexes;
      }, []);

      // 중복 항목이 없으면 종료
      if (duplicateIndexes.length === 0) {
        this.$vs.notify({
          title: '알림',
          text: '중복된 데이터가 없습니다.',
          color: 'primary',
          position: 'top-right'
        });
        return;
      }

      // 중복된 항목 제거 (뒤에서부터 제거해야 인덱스 변화에 영향 받지 않음)
      for (let i = duplicateIndexes.length - 1; i >= 0; i--) {
        this.excelData.splice(duplicateIndexes[i], 1);
      }

      // 번호 재정렬
      this.excelData.forEach((item, index) => {
        item.values[0] = index + 1;
      });

      // 체크리스트 초기화
      this.checkList = [];

      // 사용자에게 알림
      this.$vs.notify({
        title: '알림',
        text: `${duplicateIndexes.length}개의 중복 데이터가 삭제되었습니다.`,
        color: 'success',
        position: 'top-right'
      });

      // 엑셀 데이터 변경 이벤트 발생
      this.$emit('onDuplicateRemove', this.excelData);
    },

    loading(open) {
      const loadingDialog = new CustomEvent("loadingDialog", {
        detail: {
          open,
        },
      });
      window.dispatchEvent(loadingDialog);
    },
    // 엑셀을 읽어온 뒤, 사업장명 으로 사업장 데이터를 찾기위해 모든 사업장 불러오기
    async loadFieldListAll() {
      this.loading(true);
      const loadFieldList = (page) => {
        this.$store.dispatch("project/LOAD_FIELD_LIST", {
          projectId: this.projectId,
          page,
          perPage: 50,
        }).then((response) => {
          const data = response.data;
          this.fields.push(...data)
          if (response.last_page > page) {
            loadFieldList(page + 1);
          } else {
            this.loading(false);
          }
        });
      }
      await loadFieldList(1)
    },
    async loadCompanyListAll() {
      this.loading(true);
      const loadCompanyList = (page) => {
        this.$store.dispatch("company/LOAD_COMPANY_LIST", {
          projectId: this.projectId,
          page,
          perPage: 50,
        }).then((response) => {
          const data = response.data;
          this.companies.push(...data)
          if (response.last_page > page) {
            loadCompanyList(page + 1);
          } else {
            this.loading(false);
          }
        });
      }
      await loadCompanyList(1)
    },

    // 에러 메시지가 변경된 excelData를 바로 업데이트하는 메서드
    updateExcelData(data) {
      if (Array.isArray(data)) {
        // 참조를 변경하여 반응성 트리거
        this.excelData = [...data];
      }
    },

    // 엑셀데이터 파싱
    readFile(event) {
      const findFieldByFieldName = (fieldName) => {
        return this.fields.find((field) => field.field_name === fieldName);
      }

      const findCompanyByComName = (comName) => {
        return this.companies.find((company) => company.com_name === comName);
      }

      const getDataInSheet = (sheet, rowNumber, cells = []) => {
        const func = {
          rowIndex: this.excelData.length + 1,
          findFieldByFieldName: this.useFieldCell ? findFieldByFieldName : undefined,
          findCompanyByComName: this.useCompanyCell ? findCompanyByComName : undefined,
          toTel: this.setPhone,
          save: (values, errorMSG) => {
            this.excelData.push({
              values,
              errorMSG,
            });
          },
        }
        if (this.readExcelData) {
          const dataTypes = this.tableDataType;
          this.readExcelData(rowNumber, cells, dataTypes, func)
        }
      }

      // ----

      const workbook = new ExcelJS.Workbook();
      const file = event.target.files[0];
      const reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onload = () => {
        const buffer = reader.result;

        // 10000행 이상의 데이터는 읽지 않는다.
        const maxRow = 10000;
        workbook.xlsx.load(buffer).then((workbook) => {
          workbook.eachSheet((sheet) => {
            sheet.eachRow((row, rowNumber) => {
              if (rowNumber > maxRow) {
                // 10000행 이상의 데이터는 읽지 않습니다.
                return;
              }

              const cells = ['']
              let countEmptyCell = 0;
              let foundEmpty = true;

              for (let i = 1; i<row.values.length; i++) {
                const text = `${row.getCell(i).text}`.trim();
                if (!text || text === 'undefined' || text === 'null' || text === 'NaN' || text === '') {
                  if (foundEmpty) {
                    countEmptyCell++;
                  }
                } else {
                  foundEmpty = false;
                }
                cells.push(text);
              }

              // 맨 앞에 빈 셀이 2개 이상이면 데이터가 없다고 판단
              if (countEmptyCell >= 2) {
                return;
              }

              getDataInSheet(sheet, rowNumber, cells)
            });
          });
          //
          // this.excelData = data;
        });
      };


    },

    setEditExcelData(columnIndex, data, errorMSG = undefined) {
      this.$set(this.editExcelData.values, columnIndex, data);
      if (typeof errorMSG === "string") {
        this.editExcelData.errorMSG[columnIndex] = errorMSG;
      }
    },
    // 부모가 호출하는 함수
    clearData () {
      this.excelData = [];
      this.checkList = [];
    },
    // 부모가 호출하는 함수
    excelUpload() {
      const excelInput = this.$refs.excelInput;
      excelInput.value = null;
      excelInput.click();
    },
    onClickCheckRemove() {
      if (this.checkList.length < 1) return;
      this.excelData = this.excelData.filter((_, i) => !this.checkList.includes(i));
      this.excelData.forEach((_, i) => {
        _.values[0] = i + 1;
      });
      this.checkList = [];
    },
    onClickFinishExcelData(index) {
      this.$set(this.excelData, index, this.editExcelData);
      this.onClickCancelExcelData();
    },
    onClickCancelExcelData() {
      this.editExcelData = [];
      this.editExcelIndex = null;
    },
    onClickEditExcelData(index) {
      this.editExcelIndex = index;
      this.editExcelData = JSON.parse(JSON.stringify(this.excelData[index]));
    },
    onClickRemoveExcelData(index) {
      this.excelData.splice(index, 1);
    },
    onSelectField (rowIndex, columnIndex, field) {
      this.editExcelData.values[columnIndex] = {
        id: field.id,
        field_name: field.field_name,
      };
      this.editExcelData.errorMSG[columnIndex] = field.id ? "" : "입력된 데이터가 검색되지 않습니다.";
    },
  }
}
</script>

<style scoped lang="scss">
table {
  table-layout: fixed;
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    text-align: center;
    padding: 0;
  }

  th {
    font-weight: 700;
    font-size: 14px;
    line-height: 17px;

    background: #fafafa;
    border-top: 1px solid #cdcdcd;
    border-bottom: 1px solid #cdcdcd;
    padding: 16px 0;
  }

  td {
    font-weight: 400;
    font-size: 14px;
    line-height: 17px;

    border-bottom: 1px solid #cdcdcd;
    padding: 16px 0;
  }

  button {
    width: 32px;
    height: 32px;

    display: flex;
    align-items: center;
    justify-content: center;

    color: #7e72f2;
    background: none;
    border: 1px solid #7e72f2;
    border-radius: 4px;
    cursor: pointer;

    span {
      margin: 0;
    }
  }

  .check {
    justify-content: center !important;

    ::v-deep {
      span {
        margin: 0;
      }
    }
  }
}

.errMsg {
  font-weight: 400;
  font-size: 12px;
  line-height: 17px;
  text-align: center;
  color: #ea5455;
  margin-top: 2px;
}
</style>

